import { Call, Device } from "@twilio/voice-sdk"
import { useCallback, useEffect, useRef, useState } from "react"

import {
  completeDialerSession,
  createDialerSession,
  enqueueDialerQueue,
  getActiveDialerSession,
  getDialerCallByCallSid,
  getDialerCallSummaryStreamUrl,
  getDialerQueue,
  getNextDialerQueueEntry,
  reserveDialerCall,
  setDialerCallDisposition,
  setDialerCallNote,
  updateDialerQueueEntryStatus
} from "~/api/dialer"
import { getVoiceToken } from "~/api/voice"
import useGlobalStore from "~/store/global"

// One Device for the whole app, not one per component. A callback can arrive whenever, so the
// toast has to live outside the dialer — and two Devices registered on the same identity would
// leave Twilio to pick which one receives the call. Reference counted so it is created on the
// first consumer and destroyed when the last one goes.
let sharedDevice: Device | null = null
let sharedIdentity: string | null = null
// Creation is async — the token has to be fetched first — so a plain null check is not a guard.
// Two consumers both saw null and each built a Device, two registrations landed on the same
// identity, and Twilio dropped one: the socket closed straight after connecting and no call could
// be placed. Everyone awaits the same promise instead.
let sharedDevicePromise: Promise<Device> | null = null
// Unix seconds when the Device's current access token expires. Tokens are minted
// with a one-hour ttl and refreshed pre-emptively by the tokenWillExpire event —
// but that event never fires in a backgrounded or slept tab, so a laptop that
// sleeps across the hour wakes with a dead token the Device still believes is
// good. connect() then fails silently and the call sits QUEUED. This lets a
// pre-call check see the token is stale and re-mint before dialing.
let sharedTokenExp = 0
let sharedIncomingCall: Call | null = null

// What the service tells us about a callback, beyond the number it came from. A callback can
// arrive with the dialer closed, so the page has no context of its own to fall back on.
export type IncomingCaller = {
  from: string
  callSid: string
  parentSid: string
  name: string | null
  title: string | null
  peopleId: string | null
  lastCalledAt: number | null
  // Arrived while the rep was already on a call, so it is shown without a ringtone
  // and answering has to end the call in progress first.
  midCall?: boolean
}

// Twilio's own reconnect attempts run to roughly this before it gives up. Waiting
// longer than the SDK does only extends the time a rep talks to nobody.
const RECONNECT_GRACE_MS = 10000

let sharedIncoming: IncomingCaller | null = null
const incomingListeners = new Set<(v: IncomingCaller | null) => void>()

function publishIncoming(v: IncomingCaller | null) {
  sharedIncoming = v
  incomingListeners.forEach((fn) => fn(v))
}

function attachIncomingHandler(device: Device, busy: () => boolean) {
  device.on("incoming", (call: Call) => {
    // One at a time: a second callback while the first is ringing is rejected rather than
    // stacking toasts the rep cannot act on.
    if (sharedIncomingCall) {
      call.reject()
      return
    }
    // Mid-call it is offered rather than rejected, but silently: a ringtone over a
    // live conversation is heard by the person the rep is already talking to.
    const midCall = busy()
    if (midCall) {
      try {
        device.audio?.incoming(false)
      } catch {
        // older SDKs without audio controls still get the quiet toast
      }
    }
    sharedIncomingCall = call
    // From is the number the call was placed from, which for a forwarded callback is one of
    // ours rather than the caller's. The service sends who is actually calling alongside it.
    const p = call.customParameters
    const lastCalled = Number(p?.get("lastCalledAt"))
    publishIncoming({
      from: p?.get("callerNumber") ?? call.parameters?.From ?? "",
      callSid: call.parameters?.CallSid ?? "",
      parentSid: p?.get("parentSid") ?? "",
      name: p?.get("callerName") ?? null,
      title: p?.get("callerTitle") ?? null,
      peopleId: p?.get("peopleId") ?? null,
      lastCalledAt:
        Number.isFinite(lastCalled) && lastCalled > 0 ? lastCalled : null,
      midCall
    })
    const clear = () => {
      sharedIncomingCall = null
      publishIncoming(null)
    }
    call.on("cancel", clear)
    call.on("disconnect", clear)
    call.on("reject", clear)
  })
}

type DeviceStatus = "idle" | "registering" | "ready" | "error"

// Why a call could not be placed, for a message the rep can act on rather than a
// silent no-op: "mic" = microphone blocked, "token" = the line could not be
// (re)authorised, "device" = no Device at all.
export type NotReadyReason = "mic" | "token" | "device"

// Seconds-until-expiry below which the access token is treated as stale and
// re-minted before a call. Comfortably larger than a dial round-trip so a token
// cannot lapse mid-connect.
const TOKEN_REFRESH_SKEW_S = 120

// Read a JWT's exp (unix seconds) without a library. Returns 0 if it cannot be
// parsed, which forces a refresh rather than trusting an unknown token.
function tokenExpiry(jwt: string): number {
  try {
    const payload = JSON.parse(atob(jwt.split(".")[1]))
    return typeof payload.exp === "number" ? payload.exp : 0
  } catch {
    return 0
  }
}

// Trigger the browser's microphone permission prompt and confirm access. The
// Twilio Device registers fine without mic permission, so devStatus === "ready"
// is not proof the rep can be heard; this is. Tracks are stopped immediately —
// we only wanted the grant, not to hold the mic open.
async function micGranted(): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach((t) => t.stop())
    return true
  } catch {
    return false
  }
}

export function useVoiceDevice() {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const deviceRef = useRef<Device | null>(null)
  const callRef = useRef<Call | null>(null)
  const [incoming, setIncoming] = useState<IncomingCaller | null>(
    sharedIncoming
  )

  useEffect(() => {
    incomingListeners.add(setIncoming)
    setIncoming(sharedIncoming)
    return () => {
      incomingListeners.delete(setIncoming)
    }
  }, [])
  const sessionIdRef = useRef<number | null>(null)
  const [status, setStatus] = useState<DeviceStatus>("idle")
  // Why the last dial attempt could not start. The ref is written synchronously
  // inside makeCall so the caller can read the reason the instant makeCall
  // returns null (React state would still be stale in that tick); the state
  // mirror is for anything that renders from it. Both say which of mic / token /
  // device blocked the call, so the UI names it instead of leaving a
  // silently-failed call in the queue.
  const notReadyRef = useRef<NotReadyReason | null>(null)
  const [notReady, setNotReady] = useState<NotReadyReason | null>(null)
  const flagNotReady = useCallback((reason: NotReadyReason | null) => {
    notReadyRef.current = reason
    setNotReady(reason)
  }, [])
  const [onCall, setOnCall] = useState(false)
  const [reconnecting, setReconnecting] = useState(false)
  // Latched so the panel can report WHY a call ended after the fact; the caller
  // clears it when it has been shown.
  const [networkDropped, setNetworkDropped] = useState(false)
  const reconnectDeadlineRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const [muted, setMuted] = useState(false)

  // Read through a ref so this keeps ONE identity for the life of the hook.
  //
  // Memoised on the tokenInfo object, it changed every time the store replaced
  // that object, which re-ran the effect below - and that effect's cleanup
  // nulls deviceRef. A rep who pressed dial inside the gap got "Could not
  // place that call" from a device that was only briefly missing.
  const tokenInfoRef = useRef(tokenInfo)
  tokenInfoRef.current = tokenInfo

  const fetchToken = useCallback(async () => {
    const info = tokenInfoRef.current
    if (!info) throw new Error("no token info available")
    const res = await getVoiceToken(info)
    const token = res.data.token
    sharedTokenExp = tokenExpiry(token)
    return token
  }, [])

  useEffect(() => {
    if (!tokenInfo?.tenant_id || !tokenInfo?.user_id) return
    let cancelled = false

    const identity = `${tokenInfo.tenant_id}_${tokenInfo.user_id}`

    // A device already registered for this identity stays. It is only replaced when the
    // identity itself changes, which is the one case where the old registration is wrong.
    if (sharedDevice && sharedIdentity !== identity) {
      sharedDevice.destroy()
      sharedDevice = null
      sharedIncomingCall = null
      publishIncoming(null)
    }

    const setup = async () => {
      try {
        setStatus("registering")

        if (!sharedDevicePromise) {
          sharedDevicePromise = (async () => {
            const token = await fetchToken()
            const created = new Device(token, {
              logLevel:
                import.meta.env.DEV || import.meta.env.isDev ? 1 : "error"
            })
            sharedDevice = created
            sharedIdentity = identity
            attachIncomingHandler(created, () => callRef.current !== null)
            created.on("error", () => setStatus("error"))
            created.on("tokenWillExpire", async () => {
              try {
                created.updateToken(await fetchToken())
              } catch {
                setStatus("error")
              }
            })
            await created.register()
            return created
          })().catch((err) => {
            // Cleared so a later mount can retry rather than awaiting a failed promise forever.
            sharedDevicePromise = null
            throw err
          })
        }

        const device = await sharedDevicePromise
        if (cancelled) return
        deviceRef.current = device
        setStatus("ready")
      } catch {
        setStatus("error")
      }
    }

    setup()

    return () => {
      cancelled = true
      // The device deliberately outlives its consumers. It belongs to the signed-in user, not to
      // whichever component happened to mount first, and it must survive a re-render while a
      // call is up.
      //
      // Which is why the ref is NOT cleared here. Clearing it left a window -
      // from this cleanup until the next setup resolved - where the hook held
      // no device and a dial failed outright. The shared device is unaffected
      // by this effect re-running, so the ref pointing at it stays true; a
      // genuine teardown replaces it on the next setup.
    }
  }, [tokenInfo?.tenant_id, tokenInfo?.user_id, fetchToken])

  const resumeSession = useCallback(async () => {
    if (!tokenInfo) return null
    try {
      const { data } = await getActiveDialerSession(tokenInfo)
      const activeId = data?.data?.id ?? null
      sessionIdRef.current = activeId
      return activeId
    } catch {
      return null
    }
  }, [tokenInfo])

  const ensureSession = useCallback(
    async (
      callingHours?: { start: string; end: string },
      force = false,
      listId?: string | number | null
    ) => {
      if (!tokenInfo) return null
      // The resume-on-mount flow populates sessionIdRef.current on every
      // mount so a page reload can rejoin an in-progress session — but that
      // means a genuinely fresh "Start" click, on the very same mount, saw
      // this ref already set and reused that stale session instead of
      // starting a new one: duplicate CALLED status writes onto entries
      // already completed in the earlier run, and queue entries from the
      // fresh enqueue landing on top of the old ones. `force` lets
      // startSession() opt out of the cache for exactly this case, while
      // every other caller (quick-dial's calling-hours check) keeps reusing
      // a session already known to be live.
      if (!force && sessionIdRef.current) return sessionIdRef.current
      try {
        const { data } = await createDialerSession(
          tokenInfo,
          false,
          callingHours,
          listId
        )
        sessionIdRef.current = data?.data?.id ?? null
      } catch {
        sessionIdRef.current = null
      }
      return sessionIdRef.current
    },
    [tokenInfo]
  )

  const enqueueQueue = useCallback(
    async (sessionId: number, peopleIds: string[]) => {
      if (!tokenInfo) return null
      try {
        const { data } = await enqueueDialerQueue(
          tokenInfo,
          sessionId,
          peopleIds
        )
        return data?.data ?? null
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[dialer] enqueueQueue failed", sessionId, peopleIds, err)
        return null
      }
    },
    [tokenInfo]
  )

  const getNextQueueEntry = useCallback(
    async (sessionId: number) => {
      if (!tokenInfo) return null
      try {
        const { data } = await getNextDialerQueueEntry(tokenInfo, sessionId)
        return data?.data ?? null
      } catch {
        return null
      }
    },
    [tokenInfo]
  )

  // The full queue, not just the next pending entry — resuming a session needs
  // every still-PENDING person, not only the first one. Resuming on a single
  // entry meant `order` collapsed to length 1 and the very next call ending
  // satisfied goTo's oi+1 >= order.length check, completing the session with
  // the rest of the queue abandoned as PENDING forever.
  const getQueue = useCallback(
    async (sessionId: number) => {
      if (!tokenInfo) return []
      try {
        const { data } = await getDialerQueue(tokenInfo, sessionId)
        return data?.data ?? []
      } catch {
        return []
      }
    },
    [tokenInfo]
  )

  const reserveCall = useCallback(
    async (
      sessionId: number,
      queueId: number,
      calleeNumber: string,
      outboundNumber?: string,
      linkedinUrl?: string,
      listId?: string
    ) => {
      if (!tokenInfo) return null
      try {
        const { data } = await reserveDialerCall(
          tokenInfo,
          sessionId,
          queueId,
          calleeNumber,
          outboundNumber,
          linkedinUrl,
          listId
        )
        return data?.data?.call_id ?? null
      } catch (err) {
        // best-effort — voiceAnswer falls back to inserting a fresh
        // dialer_calls row when no reservation exists, so a failure here
        // shouldn't block the dial. Logged so a persistent failure is visible.
        // eslint-disable-next-line no-console
        console.error("[dialer] reserveCall failed", sessionId, queueId, err)
        return null
      }
    },
    [tokenInfo]
  )

  const markQueueEntryStatus = useCallback(
    async (
      sessionId: number,
      queueId: number,
      status: "PENDING" | "CALLED" | "SKIPPED"
    ) => {
      if (!tokenInfo) return
      try {
        await updateDialerQueueEntryStatus(
          tokenInfo,
          sessionId,
          queueId,
          status
        )
      } catch (err) {
        // best-effort — a failed status update shouldn't block the UI flow,
        // but it must not fail SILENTLY: every dialer_queue row in dev has
        // been stuck on PENDING, and a swallowed error here is the prime
        // suspect. Logged, not surfaced to the user.
        // eslint-disable-next-line no-console
        console.error(
          "[dialer] markQueueEntryStatus failed",
          sessionId,
          queueId,
          status,
          err
        )
      }
    },
    [tokenInfo]
  )

  // Every route to an active call goes through here. Answering used to set the ref and attach
  // nothing, so the ref stayed populated after the call ended — and since busy() is that ref,
  // the browser then refused every further callback and only the phone leg ever rang.
  const adoptCall = useCallback((call: Call) => {
    callRef.current = call
    setOnCall(true)
    setMuted(false)

    // A media path can die without the signalling channel noticing, which left the
    // panel reporting a call that could no longer be heard on either side. The SDK
    // says so — nothing was listening.
    const clearDeadline = () => {
      if (reconnectDeadlineRef.current) {
        clearTimeout(reconnectDeadlineRef.current)
        reconnectDeadlineRef.current = null
      }
    }

    const end = () => {
      clearDeadline()
      setReconnecting(false)
      setOnCall(false)
      callRef.current = null
    }

    const onReconnecting = () => {
      setReconnecting(true)
      clearDeadline()
      // Twilio gives up on its own eventually, but not before the rep has spent a
      // minute talking to nobody. A call that cannot be restored inside the window
      // is ended rather than left looking live.
      reconnectDeadlineRef.current = setTimeout(() => {
        setNetworkDropped(true)
        try {
          call.disconnect()
        } catch {
          // already gone; end() has run or will
        }
      }, RECONNECT_GRACE_MS)
    }

    const onReconnected = () => {
      clearDeadline()
      setReconnecting(false)
    }

    call.on("reconnecting", onReconnecting)
    call.on("reconnected", onReconnected)
    call.on("warning", (name: string) => {
      if (
        name === "ice-connectivity-lost" ||
        name === "ice-connection-disconnected"
      ) {
        onReconnecting()
      }
    })
    call.on("warning-cleared", (name: string) => {
      if (
        name === "ice-connectivity-lost" ||
        name === "ice-connection-disconnected"
      ) {
        onReconnected()
      }
    })
    call.on("disconnect", end)
    call.on("cancel", end)
    call.on("reject", end)
    call.on("error", end)
  }, [])

  const makeCall = useCallback(
    async (
      to: string,
      params: Record<string, string> = {},
      peopleId?: string,
      callingHours?: { start: string; end: string },
      linkedinUrl?: string
    ) => {
      const device = deviceRef.current
      if (!device || !tokenInfo) {
        flagNotReady("device")
        return null
      }

      // Force the microphone prompt and confirm the grant before dialing. Without
      // this a rep who never allowed the mic runs the whole queue placing calls
      // nobody can hear, and nothing gets logged. Blocking here surfaces it as
      // one clear message instead.
      if (!(await micGranted())) {
        flagNotReady("mic")
        return null
      }

      // Re-mint if the token is at or near expiry. tokenWillExpire covers a live
      // tab, but a backgrounded or slept tab misses the event and wakes with a
      // dead token the Device still trusts, so connect() fails silently and the
      // call sits QUEUED. Refreshing here is what makes the dialer self-heal
      // after the tab has been idle.
      const nowS = Math.floor(Date.now() / 1000)
      if (!sharedTokenExp || nowS >= sharedTokenExp - TOKEN_REFRESH_SKEW_S) {
        try {
          device.updateToken(await fetchToken())
          if (device.state !== "registered") await device.register()
        } catch {
          flagNotReady("token")
          return null
        }
      }
      flagNotReady(null)

      // Only queue-driven bulk dialing passes callingHours (BulkDialer4L
      // always does; Dialer4L's single quick-dial never does) — a standalone
      // quick dial has no queue to resume and shouldn't create a
      // dialer_sessions row just to be dropped, unused, at COMPLETED/ABANDONED.
      // The call still logs fine with session_id omitted: voiceAnswer treats
      // it as optional (controllers/voice.js), and dialer_calls.session_id is
      // nullable.
      if (callingHours) {
        await ensureSession(callingHours)
      }

      const call = await device.connect({
        params: {
          To: to,
          ...params,
          ...(sessionIdRef.current
            ? { session_id: String(sessionIdRef.current) }
            : {}),
          ...(peopleId ? { people_id: peopleId } : {}),
          ...(linkedinUrl ? { linkedin_url: linkedinUrl } : {}),
          user_id: String(tokenInfo.user_id),
          // voiceAnswer gates ALL caller-number selection on tenant_id:
          //
          //   let callerId = APP.TWILIO_CALLER_NUMBER
          //   if (tenant_id) { ...selectCallerNumber... }
          //
          // Without it selectCallerNumber never ran, so Boost Connect,
          // rotation and country matching were bypassed on every call and the
          // leg went out on the global default — which is what showed up on
          // the callee's phone as an unknown caller.
          tenant_id: String(tokenInfo.tenant_id)
        }
      })
      adoptCall(call)
      return call
    },
    [tokenInfo, ensureSession, adoptCall, fetchToken, flagNotReady]
  )

  const hangup = useCallback(() => {
    callRef.current?.disconnect()
    callRef.current = null
    setOnCall(false)
  }, [])

  const toggleMute = useCallback(() => {
    const call = callRef.current
    if (!call) return
    const next = !muted
    call.mute(next)
    setMuted(next)
  }, [muted])

  const sendDigits = useCallback((digits: string) => {
    // Twilio throws if the call it holds is no longer open — a queue dialer hits that
    // whenever the ref outlives the leg. A refused tone must not take the caller's
    // keypad down with it.
    try {
      callRef.current?.sendDigits(digits)
    } catch {
      return
    }
  }, [])

  // Single SSE connection covering the whole call lifecycle: status
  // transitions (queued/ringing/answered/hangup) as they happen, followed by
  // the AI summary once it's generated — replaces two separate polling loops
  // that used to hit the call-lookup endpoint on their own schedules.
  const watchCallStream = useCallback(
    (
      callSid: string | null | undefined,
      handlers: {
        onStatus?: (status: string) => void
        onSummary?: (summary: string) => void
        onFailed?: () => void
      }
    ) => {
      if (!tokenInfo || !callSid) return () => {}

      const controller = new AbortController()

      // The backend looks this call up by twilio_call_sid the instant this
      // fires (right on the browser's own "accept" event) — but that column
      // is only written once Twilio's OWN answer webhook reaches our
      // backend, a separate server-to-server round trip that can easily
      // still be in flight. A first-attempt failure here used to be
      // permanent and silent: onStatus("ANSWERED") never arrived, so a call
      // that genuinely connected and was talked on still got auto-classified
      // as "no answer" and skipped the disposition screen entirely. Retried
      // a few times with a short gap so a still-in-flight write has a chance
      // to land before giving up for real.
      const MAX_ATTEMPTS = 5
      const RETRY_DELAY_MS = 800

      const readStream = async (attempt = 1) => {
        try {
          const response = await fetch(
            getDialerCallSummaryStreamUrl(tokenInfo, callSid),
            {
              headers: { Authorization: `Bearer ${tokenInfo.token}` },
              signal: controller.signal
            }
          )
          if (!response.ok || !response.body) {
            if (attempt < MAX_ATTEMPTS && !controller.signal.aborted) {
              await new Promise((resolve) =>
                setTimeout(resolve, RETRY_DELAY_MS)
              )
              if (!controller.signal.aborted) return readStream(attempt + 1)
            }
            return
          }

          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ""

          while (true) {
            const { done, value } = await reader.read()
            if (done) return
            buffer += decoder.decode(value, { stream: true })

            let frameEnd = buffer.indexOf("\n\n")
            while (frameEnd !== -1) {
              const frame = buffer.slice(0, frameEnd)
              buffer = buffer.slice(frameEnd + 2)

              let eventType = "message"
              let dataStr = ""
              for (const line of frame.split("\n")) {
                if (line.startsWith("event:")) eventType = line.slice(6).trim()
                else if (line.startsWith("data:"))
                  dataStr += line.slice(5).trimStart()
              }

              if (!dataStr) {
                frameEnd = buffer.indexOf("\n\n")
                continue
              }

              try {
                const parsed = JSON.parse(dataStr)
                if (eventType === "status" && parsed?.status) {
                  handlers.onStatus?.(parsed.status)
                } else if (eventType === "summary" && parsed?.ai_summary) {
                  handlers.onSummary?.(parsed.ai_summary)
                  return
                } else if (eventType === "failed") {
                  // Transcription permanently failed server-side (AssemblyAI
                  // exhausted its retries) — a known, final state, distinct
                  // from "timeout" (which just means we gave up waiting, not
                  // that the backend knows it will never arrive).
                  handlers.onFailed?.()
                  return
                } else if (eventType === "timeout") {
                  handlers.onFailed?.()
                  return
                }
              } catch {
                // malformed frame — server is the source of truth, skip it
              }

              frameEnd = buffer.indexOf("\n\n")
            }
          }
        } catch {
          // aborted or network error — the caller's cleanup already handles
          // this by calling abort(), nothing further to do here
        }
      }
      readStream()

      return () => {
        controller.abort()
      }
    },
    [tokenInfo]
  )

  const endSession = useCallback(async () => {
    if (!tokenInfo || !sessionIdRef.current) return
    const sessionId = sessionIdRef.current
    sessionIdRef.current = null
    try {
      await completeDialerSession(tokenInfo, sessionId)
    } catch {
      // best-effort — a failed completion call shouldn't block the UI flow
    }
  }, [tokenInfo])

  const logCall = useCallback(
    async (
      callSid: string | null | undefined,
      {
        disposition,
        notes,
        meetingAt,
        fallbackCallId
      }: {
        disposition?: string
        notes?: string
        meetingAt?: { date?: string; time?: string } | null
        // The reserved dialer_calls row's own id, from reserveCall — set
        // BEFORE Twilio is invoked. voiceAnswer only attaches a real
        // twilio_call_sid to that row once it decides to actually dial out;
        // a call blocked before that (closed calling hours, most commonly)
        // leaves the row with no twilio_call_sid to look it up by at all,
        // even though the browser genuinely has a CallSid for its own leg.
        // Without this, ending/logging that call silently did nothing.
        fallbackCallId?: number | string | null
      }
    ) => {
      if (!tokenInfo) return

      // Failures are NOT swallowed here. The only caller catches them, tells
      // the rep the log could not be saved and keeps them on the person with
      // their disposition still typed - whereas swallowing advanced the queue
      // as though the call had been logged, so a write that timed out looked
      // exactly like one that worked.
      {
        let callId: number | string | undefined
        if (callSid) {
          try {
            const { data } = await getDialerCallByCallSid(tokenInfo, callSid)
            callId = data?.data?.id
          } catch {
            // no row with this SID — most commonly because the call never
            // got past voiceAnswer's own early-exit paths to attach one
          }
        }
        if (!callId) callId = fallbackCallId ?? undefined
        if (!callId) return

        if (disposition) {
          const meetingSetAt = meetingAt?.date
            ? new Date(
                `${meetingAt.date}T${meetingAt.time || "09:00"}`
              ).toISOString()
            : undefined
          await setDialerCallDisposition(
            tokenInfo,
            callId,
            disposition,
            meetingSetAt
          )
        }
        if (notes) {
          await setDialerCallNote(tokenInfo, callId, notes)
        }
      }
    },
    [tokenInfo]
  )

  const restoreRingtone = useCallback(() => {
    try {
      sharedDevice?.audio?.incoming(true)
    } catch {
      // nothing to restore on SDKs without audio controls
    }
  }, [])

  const answerIncoming = useCallback(() => {
    const call = sharedIncomingCall
    if (!call) return null
    // A call already up has to go before this one is adopted, or the rep is bridged
    // into two conversations and the first party stays connected to a dead line.
    if (callRef.current) {
      callRef.current.disconnect()
      callRef.current = null
      setOnCall(false)
    }
    call.accept()
    adoptCall(call)
    sharedIncomingCall = null
    publishIncoming(null)
    try {
      sharedDevice?.audio?.incoming(true)
    } catch {
      // nothing to restore on SDKs without audio controls
    }
    return call
  }, [adoptCall])

  const rejectIncoming = useCallback(() => {
    const call = sharedIncomingCall
    if (!call) return
    // Rejecting hands the caller back to the inbound TwiML, which takes a message rather than
    // dropping them.
    call.reject()
    sharedIncomingCall = null
    publishIncoming(null)
    restoreRingtone()
  }, [restoreRingtone])

  return {
    status,
    notReady,
    notReadyRef,
    clearNotReady: () => flagNotReady(null),
    incoming,
    answerIncoming,
    rejectIncoming,
    onCall,
    reconnecting,
    networkDropped,
    clearNetworkDropped: () => setNetworkDropped(false),
    muted,
    makeCall,
    hangup,
    toggleMute,
    sendDigits,
    logCall,
    watchCallStream,
    endSession,
    resumeSession,
    ensureSession,
    enqueueQueue,
    getNextQueueEntry,
    getQueue,
    reserveCall,
    markQueueEntryStatus
  }
}
